import {
  Title,
  Paper,
  Group,
  Button,
  Accordion,
  Container,
  Grid,
  useMantineTheme,
} from '@mantine/core';
import { DateRangePicker } from '@mantine/dates';
import { Calendar } from 'tabler-icons-react';
import './logs.styles.css';
import { Filter as FilterIcon } from 'tabler-icons-react';
import { useEffect, useState } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  BasicAuditLog,
  RetrieveAuditLogsRequest,
} from '../../type/retrieve-audit-logs.types';
import { IPaginationMeta } from '../../type/admin.types';
import LogsTimeline from './components/logs-timeline.component';
import LogsDetails from './components/logs-details.component';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import SearchButton from '../buttons/search-button/search-button.component';

function Logs() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>();
  const [auditLogData, setAuditLogData] = useState<BasicAuditLog[]>();
  const [selectedLog, setSelectedLog] = useState<BasicAuditLog>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const axiosPrivate = useAxiosPrivate();
  const theme = useMantineTheme();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    createRequest();
    setPage(1);
  }, []);

  useDidMountEffect(() => {
    if (dateRange && dateRange[0] === null && dateRange[1] === null) {
      resetRequest();
      setPage(1);
    }
  }, [dateRange]);

  useDidMountEffect(() => {
    if (dateRange?.[0] && dateRange?.[1]) {
      let startDate = dateRange[0];
      let offset = startDate.getTimezoneOffset();
      startDate = new Date(startDate.getTime() - offset * 60 * 1000);
      const formatStartDate =
        startDate.toISOString().split('T')[0] + ' 00:00:00';

      let endDate = dateRange[1];
      offset = endDate.getTimezoneOffset();
      endDate = new Date(endDate.getTime() - offset * 60 * 1000);
      const formatEndDate = endDate.toISOString().split('T')[0] + ' 23:59:59';

      createRequest(undefined, formatStartDate, formatEndDate, 1, search);
    } else {
      createRequest(undefined, undefined, undefined, 1, search);
    }

    setPage(1);
  }, [search]);

  const resetRequest = async () => {
    try {
      const request: RetrieveAuditLogsRequest = {
        page: 1,
        filters: {
          limit: 20,
        },
      };

      const response = await axiosPrivate.post('/web/logs', request);
      const items: BasicAuditLog[] = response.data.items;
      const metadata: IPaginationMeta = response.data.metadata;

      setPaginationData(metadata);

      setAuditLogData(items);
    } catch (err) {}
  };

  const createRequest = async (
    e?: any,
    formatStartDate?: string,
    formatEndDate?: string,
    pageToGet?: number,
    username?: string,
  ) => {
    e?.preventDefault();

    try {
      setIsLoading(true);
      const request: RetrieveAuditLogsRequest = {
        page: pageToGet ?? 1,
        filters: {
          limit: 20,
          startDate: formatStartDate,
          endDate: formatEndDate,
          username: username,
        },
      };

      if (!formatStartDate || !formatEndDate) {
        delete request.filters.startDate;
        delete request.filters.endDate;
      }

      if (!username) {
        delete request.filters.username;
      }

      const response = await axiosPrivate.post('/web/logs', request);
      const items: BasicAuditLog[] = response.data.items;
      const metadata: IPaginationMeta = response.data.metadata;
      setPaginationData(metadata);

      setAuditLogData((prev) => {
        if (prev !== undefined && pageToGet !== 1) {
          return [...prev, ...items];
        } else return items;
      });
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: any) => {
    let startDate = dateRange![0]!;
    let offset = startDate.getTimezoneOffset();
    startDate = new Date(startDate.getTime() - offset * 60 * 1000);
    const formatStartDate = startDate.toISOString().split('T')[0] + ' 00:00:00';

    let endDate = dateRange![1]!;
    offset = endDate.getTimezoneOffset();
    endDate = new Date(endDate.getTime() - offset * 60 * 1000);
    const formatEndDate = endDate.toISOString().split('T')[0] + ' 23:59:59';

    createRequest(e, formatStartDate, formatEndDate, 1);
    setPage(1);
  };

  return (
    <div>
      <Title order={2} my="lg">
        Logs
      </Title>

      <Paper
        withBorder
        radius="md"
        shadow="lg"
        style={{
          flex: 1,
          height: '100%',
          padding: '20px 0',
        }}
      >
        <Container fluid>
          <Group p="md" grow>
            {/* Search */}
            <Group>
              <SearchButton
                setSearch={setSearch}
                setPage={setPage}
                disabled={isLoading}
                search={search}
              />
            </Group>
          </Group>

          <form>
            <Accordion
              icon={<FilterIcon size={20} strokeWidth={1} color={'black'} />}
              offsetIcon={false}
              disableIconRotation
            >
              <Accordion.Item
                label={
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    Filter
                  </div>
                }
              >
                <Container pb="md" ml={0}>
                  <Grid>
                    <Grid.Col xs={6}>
                      <DateRangePicker
                        label="Date"
                        dayStyle={(date) =>
                          date.getDate() === new Date().getDate() &&
                          date.getMonth() === new Date().getMonth() &&
                          (dateRange?.length !== 2 ||
                            (dateRange[0] === null && dateRange[1] === null))
                            ? {
                                backgroundColor: theme.colors.blue[6],
                                color: theme.white,
                              }
                            : {}
                        }
                        icon={<Calendar size={16} />}
                        value={dateRange}
                        onChange={(e) => {
                          setDateRange(e);
                        }}
                        allowSingleDateInRange
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                  <Button
                    mt="lg"
                    onClick={handleSubmit}
                    disabled={
                      dateRange === undefined ||
                      (dateRange !== undefined &&
                        (dateRange[0] === null || dateRange[1] === null))
                    }
                  >
                    Apply
                  </Button>
                </Container>
              </Accordion.Item>
            </Accordion>
          </form>
          <Container fluid p="xl">
            <Grid align="stretch">
              <Grid.Col xs={5}>
                <LogsTimeline
                  auditLogData={auditLogData}
                  setSelectedLog={setSelectedLog}
                  createRequest={createRequest}
                  setPage={setPage}
                  page={page}
                  paginationData={paginationData}
                  dateRange={dateRange}
                  search={search}
                />
              </Grid.Col>
              <Grid.Col xs={7}>
                <Group sx={{ height: '100%', width: '100%' }} align="start">
                  {/* <Divider sx={{ height: '100%' }} orientation='vertical' /> */}

                  {selectedLog && <LogsDetails selectedLog={selectedLog} />}
                </Group>
              </Grid.Col>
            </Grid>
          </Container>
        </Container>
      </Paper>
    </div>
  );
}

export default Logs;
